<template>
  <div class="checkbox-card" @click="handleCardClick">
    <View direction="column" gap="sm">
      <View direction="row" align="center" gap="sm">
        <Icon v-if="icon" :name="icon" size="small" />
        <Text variant="label-medium">{{ title }}</Text>
        <!-- For a badge or marker that belongs beside the title ("New", "Beta"). -->
        <slot name="title-suffix" />
      </View>
      <Text v-if="description" variant="body-medium" color="secondary">
        {{ description }}
      </Text>
      <slot />
    </View>
    <div @click.stop>
      <Checkbox ref="checkboxRef" v-model="model" :name="name" :value="value" :disabled="disabled" v-bind="$attrs" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';

import { IconName } from '../values/icons.const';
import Checkbox from './checkbox.vue';
import Icon from './icon.vue';
import Text from './text.vue';
import View from './view/view.vue';

interface CheckboxCardProps {
  name?: string;
  value?: any;
  modelValue?: any;
  title: string;
  description?: string;
  icon?: IconName;
  disabled?: boolean;
}

defineOptions({ inheritAttrs: false });

const props = withDefaults(defineProps<CheckboxCardProps>(), {
  disabled: false,
});

const model = defineModel<boolean | null | undefined | string[]>();
const checkboxRef = ref<InstanceType<typeof Checkbox>>();

function handleCardClick() {
  if (props.disabled) return;

  const checkboxElement = checkboxRef.value?.$el?.querySelector('input[type="checkbox"]');
  if (checkboxElement) {
    checkboxElement.click();
  }
}
</script>

<style scoped lang="scss">
.checkbox-card {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: var(--t-spacing-md);
  width: 100%;
  position: relative;
  gap: var(--t-spacing-sm);

  border: 1px solid var(--t-color-border-secondary);
  border-radius: var(--t-border-radius-md);
  background: var(--t-color-neutral-white);
  cursor: pointer;

  &:has(input:checked) {
    border-color: var(--t-color-neutral-950);
  }

  &:has(input:disabled) {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:has(input:focus-visible) {
    border-color: var(--t-color-neutral-950);
    outline: 2px solid var(--t-color-neutral-950);
    outline-offset: 2px;
  }

  &:hover:not(:has(input:disabled)) {
    border-color: var(--t-color-neutral-700);
  }
}
</style>
